import { SocialPlatform } from '@/api/profile/queries';

export interface PlatformMeta {
  label: string;
  placeholder: string;
  type: 'url' | 'handle';
  validate: (_value: string) => string;
}

export const URL_REGEX =
  /^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/;

const getHostname = (value: string): string | null => {
  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    return new URL(withProtocol).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
};

const getPathname = (value: string): string => {
  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    return new URL(withProtocol).pathname;
  } catch {
    return '';
  }
};

const hostMatches = (host: string, domains: string[]): boolean =>
  domains.some((d) => host === d || host.endsWith(`.${d}`));

const validateGenericUrl = (value: string): string =>
  URL_REGEX.test(value) ? '' : 'Enter a valid URL';

const validatePlatformUrl =
  (domains: string[], domainLabel: string, pathPattern?: RegExp, pathHint?: string) =>
  (value: string): string => {
    const generic = validateGenericUrl(value);
    if (generic) return generic;
    const host = getHostname(value);
    if (!host || !hostMatches(host, domains)) return `Enter a valid ${domainLabel} URL`;
    if (pathPattern) {
      const path = getPathname(value);
      if (!pathPattern.test(path)) return pathHint ?? `Enter a valid ${domainLabel} URL`;
    }
    return '';
  };

const validateHandle =
  (pattern: RegExp, hint: string) =>
  (value: string): string => {
    const handle = value.replace(/^@/, '');
    return pattern.test(handle) ? '' : hint;
  };

export const PLATFORMS: Record<SocialPlatform, PlatformMeta> = {
  LinkedIn: {
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/in/username',
    type: 'url',
    validate: validatePlatformUrl(
      ['linkedin.com'],
      'LinkedIn',
      /^\/(in|company|pub|school)\/[^/]+\/?/i,
      'Enter a valid LinkedIn profile URL (e.g. https://linkedin.com/in/username)',
    ),
  },
  X: {
    label: 'X',
    placeholder: 'https://x.com/username',
    type: 'url',
    validate: validatePlatformUrl(
      ['x.com', 'twitter.com'],
      'X',
      /^\/[A-Za-z0-9_]{1,15}\/?$/,
      'Enter a valid X profile URL (e.g. https://x.com/username)',
    ),
  },
  Telegram: {
    label: 'Telegram',
    placeholder: '@username',
    type: 'handle',
    validate: validateHandle(
      /^[A-Za-z][A-Za-z0-9_]{4,31}$/,
      'Enter a valid Telegram handle (5-32 chars, letters, digits, underscores)',
    ),
  },
  WhatsApp: {
    label: 'WhatsApp',
    placeholder: '+1 555 123 4567',
    type: 'handle',
    validate: (value: string): string => {
      const digits = value.replace(/[\s\-().]/g, '').replace(/^\+/, '');
      if (!/^\d{7,15}$/.test(digits)) {
        return 'Enter a valid phone number (7-15 digits, optional + prefix)';
      }
      return '';
    },
  },
  WeChat: {
    label: 'WeChat',
    placeholder: 'WeChat ID',
    type: 'handle',
    validate: validateHandle(
      /^[A-Za-z][A-Za-z0-9_-]{5,19}$/,
      'Enter a valid WeChat ID (6-20 chars, starts with a letter, letters/digits/_/-)',
    ),
  },
  Instagram: {
    label: 'Instagram',
    placeholder: '@username',
    type: 'handle',
    validate: validateHandle(
      /^(?!.*\.\.)(?!.*\.$)[A-Za-z0-9._]{1,30}$/,
      'Enter a valid Instagram handle (1-30 chars, letters, digits, dots, underscores)',
    ),
  },
  Facebook: {
    label: 'Facebook',
    placeholder: 'https://facebook.com/username',
    type: 'url',
    validate: validatePlatformUrl(
      ['facebook.com', 'fb.com'],
      'Facebook',
      /^\/[^/]+\/?/,
      'Enter a valid Facebook profile URL (e.g. https://facebook.com/username)',
    ),
  },
  Discord: {
    label: 'Discord',
    placeholder: '@username',
    type: 'handle',
    validate: (value: string): string => {
      const handle = value.replace(/^@/, '');
      // New format: 2-32 chars, lowercase letters/digits/underscores/dots, no consecutive dots.
      const newFormat = /^(?!.*\.\.)[a-z0-9._]{2,32}$/;
      // Legacy format: name#discriminator (1-32 chars + # + 4 digits).
      const legacyFormat = /^.{1,32}#\d{4}$/;
      if (newFormat.test(handle) || legacyFormat.test(handle)) return '';
      return 'Enter a valid Discord username (2-32 chars: lowercase letters, digits, _ and .)';
    },
  },
  Website: {
    label: 'Website',
    placeholder: 'https://...',
    type: 'url',
    validate: validateGenericUrl,
  },
};

export const PLATFORM_OPTIONS = (Object.entries(PLATFORMS) as [SocialPlatform, PlatformMeta][]).map(
  ([value, meta]) => ({ value, label: meta.label }),
);

// Defensive lookup: persisted data may carry a `platform` value the FE doesn't yet recognize
// (legacy enum entries, or values added on the BE before FE catches up). Falling back to
// `Website` (the generic catch-all) keeps the row rendered with a usable placeholder and a
// permissive validator, instead of throwing on `.placeholder`/`.validate` access.
export const getPlatformMeta = (platform: SocialPlatform): PlatformMeta =>
  PLATFORMS[platform] ?? PLATFORMS.Website;

export const isUrlPlatform = (platform: SocialPlatform): boolean =>
  getPlatformMeta(platform).type === 'url';

export const validatePlatformValue = (platform: SocialPlatform, value: string): string =>
  getPlatformMeta(platform).validate(value);

export const getSocialHref = (platform: SocialPlatform, value: string): string | null => {
  if (!value) return null;

  if (isUrlPlatform(platform)) {
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  }

  const handle = value.replace(/^@/, '');

  switch (platform) {
    case 'Telegram':
      return `https://t.me/${handle}`;
    case 'WhatsApp': {
      const digits = value.replace(/[\s\-().+]/g, '');
      return `https://wa.me/${digits}`;
    }
    case 'Instagram':
      return `https://instagram.com/${handle}`;
    default:
      return null;
  }
};
